import { ProductCategory } from '@collinsonx/utils/generatedTypes/graphql';
import { useFastTrack } from 'config/features';
import { useCustomBrandName } from 'hooks/useCustomBrandName';
import useGuestDetails from 'hooks/useGuestDetails';
import useLocale from 'hooks/useLocale';
import useProduct from 'hooks/useProduct';
import { RenderElement } from 'types/components';
import { replaceTranslationPlaceholder } from 'utils/translations';

type CopyText = RenderElement | undefined;

type CategoryConfig = {
  copy: Partial<{
    [key in ProductCategory]: {
      arrivalTimeExplanation: CopyText;
      cancellationPolicy: {
        line1: CopyText;
        line2?: CopyText;
      };
      errors: {
        capacity: CopyText;
      };
      guestsPolicy: CopyText;
      importantNotes: {
        one: CopyText;
        three:
          | {
              line1: string;
              line2: string;
            }
          | CopyText;
        two: CopyText;
      };
      terms: {
        line1: CopyText;
        line2: CopyText;
        link: CopyText;
      };
      timeSlotsTimeZoneDisclaimer: CopyText;
    };
  }>;
};

const useProductCategory = () => {
  const { excludedGuestFromPartyCountString } = useGuestDetails();
  const {
    accessPeriod,
    cancellationPeriod,
    category: productCategory,
    guests,
    maxPartySize,
  } = useProduct();
  const { booking, fastTrack } = useLocale();
  const translations = useLocale();

  const membership = useCustomBrandName();

  const hasSpecialGroupRules = Object.values(guests).some(
    (guest) => !guest.isPartySizeImpacted
  );

  const maximumGuests = Object.values(guests).every(
    (guest) =>
      guest.isChargeable === true && guest.maxCountPerGuestType === 9999
  );
  const groupSizeDesciption = maximumGuests
    ? translations.booking.guestDetails.unlimitedDescription
    : hasSpecialGroupRules
    ? translations.booking.guestDetails.description
    : translations.booking.guestDetails.shortDescription;

  const categoryConfig: CategoryConfig = {
    copy: {
      [ProductCategory.FastTrack]: {
        arrivalTimeExplanation: fastTrack.arrivalTimeDescription,
        cancellationPolicy: {
          line1: fastTrack.policies.cancellation,
        },
        errors: {
          capacity: replaceTranslationPlaceholder(fastTrack.errors.capacity, {
            guests: maxPartySize,
          }),
        },
        guestsPolicy: replaceTranslationPlaceholder(fastTrack.policies.guests, {
          guests: maxPartySize,
        }),
        importantNotes: {
          one: fastTrack.importantNotes.one,
          three: fastTrack.importantNotes.three,
          two: fastTrack.importantNotes.two,
        },
        terms: {
          line1: fastTrack.policies.terms.line1,
          line2: fastTrack.policies.terms.line2,
          link: fastTrack.policies.terms.link,
        },
        timeSlotsTimeZoneDisclaimer: fastTrack.timeSlotsDisclaimer,
      },
      [ProductCategory.Lounge]: {
        arrivalTimeExplanation: booking.availableSlots.description,
        cancellationPolicy: {
          line1: replaceTranslationPlaceholder(
            booking.availableSlots.cancellationPolicy.descriptionLine1,
            { time: cancellationPeriod }
          ),
          line2: replaceTranslationPlaceholder(
            booking.availableSlots.cancellationPolicy.descriptionLine2,
            { time: cancellationPeriod }
          ),
        },
        errors: {
          capacity: replaceTranslationPlaceholder(
            booking.guestDetails.errors.capacity,
            {
              excluded: excludedGuestFromPartyCountString,
              guests: maxPartySize,
            }
          ),
        },
        guestsPolicy: replaceTranslationPlaceholder(groupSizeDesciption, {
          excluded: excludedGuestFromPartyCountString,
          guests: maxPartySize,
        }),
        importantNotes: {
          one: replaceTranslationPlaceholder(
            booking.confirmationPayment.outcome.succesful.importantNotes.notes
              .one,
            { membershipType: membership }
          ),
          three: replaceTranslationPlaceholder(
            booking.confirmationPayment.outcome.succesful.importantNotes.notes
              .three,
            { time: cancellationPeriod }
          ),
          two: replaceTranslationPlaceholder(
            booking.confirmationPayment.outcome.succesful.importantNotes.notes
              .two,
            { accessPeriod }
          ),
        },
        terms: {
          line1: booking.guestDetails.loungeTerms.line1,
          line2: booking.guestDetails.loungeTerms.line2,
          link: booking.guestDetails.loungeTerms.link,
        },
        timeSlotsTimeZoneDisclaimer: booking.availableSlots.arrivalDescription,
      },
    },
  };

  return {
    displayProductCopyOf: () => {
      if (productCategory) {
        // If the product is Fast Track but the feature flag is OFF, fall back to Lounge copy
        if (productCategory === ProductCategory.FastTrack && !useFastTrack) {
          return categoryConfig.copy[ProductCategory.Lounge];
        }
        return (
          categoryConfig.copy[productCategory] ??
          categoryConfig.copy[ProductCategory.Lounge]
        );
      }
      return categoryConfig.copy[ProductCategory.Lounge];
    },
  };
};

export default useProductCategory;
